import { useState, useEffect } from 'react'
import { Box, Tabs, Tab, Grid, TextField, MenuItem, FormControl, InputLabel, Select, Chip, Typography, Divider } from '@mui/material'
import { Add, Edit, Delete, LocalGasStation } from '@mui/icons-material'
import { PageHeader, Button, DataTable, StatusChip, Card, Modal } from '@components/common'
import { BuildingForm, GeneratorForm } from '@components/facilities'
import { toast } from 'react-toastify'
import api from '@services/api'

const buildingColumns = [
  { field: 'name', headerName: 'Name', sortable: true },
  { field: 'code', headerName: 'Code', sortable: true },
  { field: 'floors', headerName: 'Floors', sortable: true },
  { field: 'address', headerName: 'Address', sortable: true },
  {
    field: 'is_active',
    headerName: 'Status',
    renderCell: (value) => (
      <StatusChip status={value ? 'active' : 'inactive'} />
    ),
  },
]

const generatorColumns = [
  { field: 'name', headerName: 'Name', sortable: true },
  { field: 'capacity_kva', headerName: 'Capacity (KVA)', sortable: true },
  { field: 'fuel_type', headerName: 'Fuel Type', sortable: true },
  {
    field: 'last_service_date',
    headerName: 'Last Service',
    renderCell: (value) => value ? new Date(value).toLocaleDateString() : '-',
  },
  {
    field: 'status',
    headerName: 'Status',
    renderCell: (value) => <StatusChip status={value} />,
  },
]

const UTILITY_TYPES = [
  { value: 'power', label: 'Power/Electricity', unit: 'kWh' },
  { value: 'water', label: 'Water', unit: 'm³' },
  { value: 'medical_gas', label: 'Medical Gas', unit: 'm³' },
  { value: 'hvac', label: 'HVAC', unit: 'kWh' },
  { value: 'steam', label: 'Steam', unit: 'kg' },
]

const utilityReadingColumns = [
  {
    field: 'reading_date',
    headerName: 'Date',
    sortable: true,
    renderCell: (value) => value ? new Date(value).toLocaleDateString() : '-',
  },
  {
    field: 'utility_name',
    headerName: 'Utility',
    renderCell: (_, row) => (
      <Chip
        label={UTILITY_TYPES.find((t) => t.value === row.utility_type)?.label || row.utility_name}
        size="small"
        variant="outlined"
      />
    ),
  },
  { field: 'building_name', headerName: 'Building', sortable: true },
  {
    field: 'reading_value',
    headerName: 'Reading',
    renderCell: (value, row) => `${Number(value).toLocaleString()} ${row.unit}`,
  },
  { field: 'recorded_by_name', headerName: 'Recorded By' },
]

const OXYGEN_PLANT_STATUS = [
  { value: 'operational', label: 'Operational', color: 'success' },
  { value: 'maintenance', label: 'Under Maintenance', color: 'warning' },
  { value: 'offline', label: 'Offline', color: 'error' },
]

const oxygenPlantColumns = [
  { field: 'name', headerName: 'Name', sortable: true },
  { field: 'location_name', headerName: 'Location', sortable: true },
  { field: 'capacity', headerName: 'Capacity', sortable: true },
  {
    field: 'status',
    headerName: 'Status',
    renderCell: (value) => {
      const status = OXYGEN_PLANT_STATUS.find((s) => s.value === value)
      return (
        <Chip
          label={status?.label || value}
          size="small"
          color={status?.color || 'default'}
        />
      )
    },
  },
  {
    field: 'created_at',
    headerName: 'Added',
    renderCell: (value) => value ? new Date(value).toLocaleDateString() : '-',
  },
]

const oxygenPlantReadingColumns = [
  {
    field: 'reading_date',
    headerName: 'Date/Time',
    sortable: true,
    renderCell: (value) => value ? new Date(value).toLocaleString() : '-',
  },
  { field: 'plant_name', headerName: 'Plant', sortable: true },
  {
    field: 'output_pressure',
    headerName: 'Pressure (PSI)',
    align: 'right',
    renderCell: (value) => value ? Number(value).toFixed(2) : '-',
  },
  {
    field: 'purity_percentage',
    headerName: 'Purity (%)',
    align: 'right',
    renderCell: (value) => value ? `${Number(value).toFixed(2)}%` : '-',
  },
  {
    field: 'production_volume',
    headerName: 'Volume (L)',
    align: 'right',
    renderCell: (value) => value ? Number(value).toLocaleString() : '-',
  },
  { field: 'recorded_by_name', headerName: 'Recorded By' },
]

const FacilitiesList = () => {
  const [activeTab, setActiveTab] = useState(0)
  const [buildings, setBuildings] = useState([])
  const [generators, setGenerators] = useState([])
  const [utilities, setUtilities] = useState([])
  const [utilityReadings, setUtilityReadings] = useState([])
  const [oxygenPlants, setOxygenPlants] = useState([])
  const [oxygenPlantReadings, setOxygenPlantReadings] = useState([])
  const [locations, setLocations] = useState([])
  const [loading, setLoading] = useState(true)
  const [buildingFormOpen, setBuildingFormOpen] = useState(false)
  const [generatorFormOpen, setGeneratorFormOpen] = useState(false)
  const [utilityFormOpen, setUtilityFormOpen] = useState(false)
  const [oxygenPlantFormOpen, setOxygenPlantFormOpen] = useState(false)
  const [oxygenReadingFormOpen, setOxygenReadingFormOpen] = useState(false)
  const [selectedBuilding, setSelectedBuilding] = useState(null)
  const [selectedGenerator, setSelectedGenerator] = useState(null)
  const [selectedReading, setSelectedReading] = useState(null)
  const [selectedOxygenPlant, setSelectedOxygenPlant] = useState(null)
  const [selectedOxygenReading, setSelectedOxygenReading] = useState(null)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [deleteType, setDeleteType] = useState(null)
  const [deleting, setDeleting] = useState(false)
  const [saving, setSaving] = useState(false)
  const [oxygenSubTab, setOxygenSubTab] = useState(0) // 0 = Plants, 1 = Readings
  const [utilityFormData, setUtilityFormData] = useState({
    utility: '',
    building: '',
    reading_value: '',
    unit: '',
    reading_date: '',
    notes: '',
  })
  const [oxygenPlantFormData, setOxygenPlantFormData] = useState({
    name: '',
    location: '',
    capacity: '',
    status: 'operational',
  })
  const [oxygenReadingFormData, setOxygenReadingFormData] = useState({
    plant: '',
    reading_date: '',
    output_pressure: '',
    purity_percentage: '',
    production_volume: '',
    notes: '',
  })

  const fetchData = async () => {
    try {
      setLoading(true)
      const [buildingsRes, generatorsRes, utilitiesRes, readingsRes, plantsRes, plantReadingsRes, locationsRes] = await Promise.all([
        api.get('/facilities/buildings/'),
        api.get('/facilities/generators/'),
        api.get('/facilities/utilities/'),
        api.get('/facilities/utility-readings/'),
        api.get('/facilities/oxygen-plants/'),
        api.get('/facilities/oxygen-plant-readings/'),
        api.get('/assets/locations/'),
      ])
      setBuildings(buildingsRes.data.results || buildingsRes.data)
      setGenerators(generatorsRes.data.results || generatorsRes.data)
      setUtilities(utilitiesRes.data.results || utilitiesRes.data)
      setUtilityReadings(readingsRes.data.results || readingsRes.data)
      setOxygenPlants(plantsRes.data.results || plantsRes.data)
      setOxygenPlantReadings(plantReadingsRes.data.results || plantReadingsRes.data)
      setLocations(locationsRes.data.results || locationsRes.data)
    } catch (error) {
      console.error('Error fetching facilities:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [])

  const handleAddClick = () => {
    if (activeTab === 0) {
      setSelectedBuilding(null)
      setBuildingFormOpen(true)
    } else if (activeTab === 1) {
      setSelectedGenerator(null)
      setGeneratorFormOpen(true)
    } else if (activeTab === 2) {
      setSelectedReading(null)
      const today = new Date().toISOString().split('T')[0]
      setUtilityFormData({
        utility: '',
        building: '',
        reading_value: '',
        unit: 'kWh',
        reading_date: today,
        notes: '',
      })
      setUtilityFormOpen(true)
    } else if (activeTab === 3) {
      if (oxygenSubTab === 0) {
        // Add Oxygen Plant
        setSelectedOxygenPlant(null)
        setOxygenPlantFormData({
          name: '',
          location: '',
          capacity: '',
          status: 'operational',
        })
        setOxygenPlantFormOpen(true)
      } else {
        // Add Oxygen Reading
        setSelectedOxygenReading(null)
        const now = new Date().toISOString().slice(0, 16)
        setOxygenReadingFormData({
          plant: '',
          reading_date: now,
          output_pressure: '',
          purity_percentage: '',
          production_volume: '',
          notes: '',
        })
        setOxygenReadingFormOpen(true)
      }
    }
  }

  const handleEditReading = (reading) => {
    setSelectedReading(reading)
    setUtilityFormData({
      utility: reading.utility || '',
      building: reading.building || '',
      reading_value: reading.reading_value || '',
      unit: reading.unit || '',
      reading_date: reading.reading_date || '',
      notes: reading.notes || '',
    })
    setUtilityFormOpen(true)
  }

  const handleSaveUtilityReading = async () => {
    if (!utilityFormData.utility || !utilityFormData.building || !utilityFormData.reading_value) {
      toast.error('Please fill in required fields')
      return
    }

    setSaving(true)
    try {
      if (selectedReading) {
        await api.patch(`/facilities/utility-readings/${selectedReading.id}/`, utilityFormData)
        toast.success('Utility reading updated')
      } else {
        await api.post('/facilities/utility-readings/', utilityFormData)
        toast.success('Utility reading added')
      }
      setUtilityFormOpen(false)
      fetchData()
    } catch (error) {
      console.error('Error saving utility reading:', error)
      toast.error('Failed to save utility reading')
    } finally {
      setSaving(false)
    }
  }

  const handleEditOxygenPlant = (plant) => {
    setSelectedOxygenPlant(plant)
    setOxygenPlantFormData({
      name: plant.name || '',
      location: plant.location || '',
      capacity: plant.capacity || '',
      status: plant.status || 'operational',
    })
    setOxygenPlantFormOpen(true)
  }

  const handleSaveOxygenPlant = async () => {
    if (!oxygenPlantFormData.name || !oxygenPlantFormData.capacity) {
      toast.error('Please fill in required fields')
      return
    }

    setSaving(true)
    try {
      if (selectedOxygenPlant) {
        await api.patch(`/facilities/oxygen-plants/${selectedOxygenPlant.id}/`, oxygenPlantFormData)
        toast.success('Oxygen plant updated')
      } else {
        await api.post('/facilities/oxygen-plants/', oxygenPlantFormData)
        toast.success('Oxygen plant added')
      }
      setOxygenPlantFormOpen(false)
      fetchData()
    } catch (error) {
      console.error('Error saving oxygen plant:', error)
      toast.error('Failed to save oxygen plant')
    } finally {
      setSaving(false)
    }
  }

  const handleEditOxygenReading = (reading) => {
    setSelectedOxygenReading(reading)
    setOxygenReadingFormData({
      plant: reading.plant || '',
      reading_date: reading.reading_date ? reading.reading_date.slice(0, 16) : '',
      output_pressure: reading.output_pressure || '',
      purity_percentage: reading.purity_percentage || '',
      production_volume: reading.production_volume || '',
      notes: reading.notes || '',
    })
    setOxygenReadingFormOpen(true)
  }

  const handleSaveOxygenReading = async () => {
    if (!oxygenReadingFormData.plant || !oxygenReadingFormData.reading_date) {
      toast.error('Please fill in required fields')
      return
    }

    setSaving(true)
    try {
      if (selectedOxygenReading) {
        await api.patch(`/facilities/oxygen-plant-readings/${selectedOxygenReading.id}/`, oxygenReadingFormData)
        toast.success('Oxygen reading updated')
      } else {
        await api.post('/facilities/oxygen-plant-readings/', oxygenReadingFormData)
        toast.success('Oxygen reading added')
      }
      setOxygenReadingFormOpen(false)
      fetchData()
    } catch (error) {
      console.error('Error saving oxygen reading:', error)
      toast.error('Failed to save oxygen reading')
    } finally {
      setSaving(false)
    }
  }

  const handleEditBuilding = (building) => {
    setSelectedBuilding(building)
    setBuildingFormOpen(true)
  }

  const handleEditGenerator = (generator) => {
    setSelectedGenerator(generator)
    setGeneratorFormOpen(true)
  }

  const handleDeleteClick = (item, type) => {
    if (type === 'building') {
      setSelectedBuilding(item)
      setDeleteType('building')
    } else if (type === 'generator') {
      setSelectedGenerator(item)
      setDeleteType('generator')
    } else if (type === 'reading') {
      setSelectedReading(item)
      setDeleteType('reading')
    } else if (type === 'oxygen_plant') {
      setSelectedOxygenPlant(item)
      setDeleteType('oxygen_plant')
    } else if (type === 'oxygen_reading') {
      setSelectedOxygenReading(item)
      setDeleteType('oxygen_reading')
    }
    setDeleteModalOpen(true)
  }

  const handleDelete = async () => {
    setDeleting(true)
    try {
      if (deleteType === 'building') {
        await api.delete(`/facilities/buildings/${selectedBuilding.id}/`)
      } else if (deleteType === 'generator') {
        await api.delete(`/facilities/generators/${selectedGenerator.id}/`)
      } else if (deleteType === 'reading') {
        await api.delete(`/facilities/utility-readings/${selectedReading.id}/`)
      } else if (deleteType === 'oxygen_plant') {
        await api.delete(`/facilities/oxygen-plants/${selectedOxygenPlant.id}/`)
      } else if (deleteType === 'oxygen_reading') {
        await api.delete(`/facilities/oxygen-plant-readings/${selectedOxygenReading.id}/`)
      }
      fetchData()
      setDeleteModalOpen(false)
      setSelectedBuilding(null)
      setSelectedGenerator(null)
      setSelectedReading(null)
      setSelectedOxygenPlant(null)
      setSelectedOxygenReading(null)
    } catch (error) {
      console.error('Error deleting:', error)
      toast.error('Failed to delete item')
    } finally {
      setDeleting(false)
    }
  }

  const getDeleteItemName = () => {
    if (deleteType === 'building') return selectedBuilding?.name
    if (deleteType === 'generator') return selectedGenerator?.name
    if (deleteType === 'oxygen_plant') return selectedOxygenPlant?.name
    if (deleteType === 'oxygen_reading') return `reading from ${selectedOxygenReading?.reading_date}`
    return `reading from ${selectedReading?.reading_date}`
  }

  const getDeleteTitle = () => {
    if (deleteType === 'building') return 'Delete Building'
    if (deleteType === 'generator') return 'Delete Generator'
    if (deleteType === 'oxygen_plant') return 'Delete Oxygen Plant'
    if (deleteType === 'oxygen_reading') return 'Delete Oxygen Reading'
    return 'Delete Reading'
  }

  const buildingActionsColumn = {
    field: 'actions',
    headerName: '',
    width: 100,
    renderCell: (_, row) => (
      <Box display="flex" gap={1}>
        <Button
          size="small"
          variant="text"
          onClick={(e) => {
            e.stopPropagation()
            handleEditBuilding(row)
          }}
        >
          <Edit fontSize="small" />
        </Button>
        <Button
          size="small"
          variant="text"
          color="error"
          onClick={(e) => {
            e.stopPropagation()
            handleDeleteClick(row, 'building')
          }}
        >
          <Delete fontSize="small" />
        </Button>
      </Box>
    ),
  }

  const generatorActionsColumn = {
    field: 'actions',
    headerName: '',
    width: 100,
    renderCell: (_, row) => (
      <Box display="flex" gap={1}>
        <Button
          size="small"
          variant="text"
          onClick={(e) => {
            e.stopPropagation()
            handleEditGenerator(row)
          }}
        >
          <Edit fontSize="small" />
        </Button>
        <Button
          size="small"
          variant="text"
          color="error"
          onClick={(e) => {
            e.stopPropagation()
            handleDeleteClick(row, 'generator')
          }}
        >
          <Delete fontSize="small" />
        </Button>
      </Box>
    ),
  }

  const utilityReadingActionsColumn = {
    field: 'actions',
    headerName: '',
    width: 100,
    renderCell: (_, row) => (
      <Box display="flex" gap={1}>
        <Button
          size="small"
          variant="text"
          onClick={(e) => {
            e.stopPropagation()
            handleEditReading(row)
          }}
        >
          <Edit fontSize="small" />
        </Button>
        <Button
          size="small"
          variant="text"
          color="error"
          onClick={(e) => {
            e.stopPropagation()
            handleDeleteClick(row, 'reading')
          }}
        >
          <Delete fontSize="small" />
        </Button>
      </Box>
    ),
  }

  const oxygenPlantActionsColumn = {
    field: 'actions',
    headerName: '',
    width: 100,
    renderCell: (_, row) => (
      <Box display="flex" gap={1}>
        <Button
          size="small"
          variant="text"
          onClick={(e) => {
            e.stopPropagation()
            handleEditOxygenPlant(row)
          }}
        >
          <Edit fontSize="small" />
        </Button>
        <Button
          size="small"
          variant="text"
          color="error"
          onClick={(e) => {
            e.stopPropagation()
            handleDeleteClick(row, 'oxygen_plant')
          }}
        >
          <Delete fontSize="small" />
        </Button>
      </Box>
    ),
  }

  const oxygenReadingActionsColumn = {
    field: 'actions',
    headerName: '',
    width: 100,
    renderCell: (_, row) => (
      <Box display="flex" gap={1}>
        <Button
          size="small"
          variant="text"
          onClick={(e) => {
            e.stopPropagation()
            handleEditOxygenReading(row)
          }}
        >
          <Edit fontSize="small" />
        </Button>
        <Button
          size="small"
          variant="text"
          color="error"
          onClick={(e) => {
            e.stopPropagation()
            handleDeleteClick(row, 'oxygen_reading')
          }}
        >
          <Delete fontSize="small" />
        </Button>
      </Box>
    ),
  }

  const getAddButtonText = () => {
    if (activeTab === 0) return 'Add Building'
    if (activeTab === 1) return 'Add Generator'
    if (activeTab === 2) return 'Add Reading'
    if (activeTab === 3) return oxygenSubTab === 0 ? 'Add Plant' : 'Add Reading'
    return 'Add'
  }

  return (
    <Box>
      <PageHeader
        title="Facilities"
        subtitle="Manage buildings and utility infrastructure"
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Facilities' },
        ]}
        actions={
          <Button startIcon={<Add />} onClick={handleAddClick}>
            {getAddButtonText()}
          </Button>
        }
      />

      <Card>
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          sx={{ mb: 3 }}
        >
          <Tab label="Buildings" />
          <Tab label="Generators" />
          <Tab label="Utilities" />
          <Tab label="Oxygen Plants" icon={<LocalGasStation />} iconPosition="start" />
        </Tabs>

        {activeTab === 0 && (
          <DataTable
            columns={[...buildingColumns, buildingActionsColumn]}
            data={buildings}
            loading={loading}
            searchPlaceholder="Search buildings..."
            emptyMessage="No buildings found"
          />
        )}

        {activeTab === 1 && (
          <DataTable
            columns={[...generatorColumns, generatorActionsColumn]}
            data={generators}
            loading={loading}
            searchPlaceholder="Search generators..."
            emptyMessage="No generators found"
          />
        )}

        {activeTab === 2 && (
          <DataTable
            columns={[...utilityReadingColumns, utilityReadingActionsColumn]}
            data={utilityReadings}
            loading={loading}
            searchPlaceholder="Search utility readings..."
            emptyMessage="No utility readings found"
          />
        )}

        {activeTab === 3 && (
          <Box>
            <Tabs
              value={oxygenSubTab}
              onChange={(_, newValue) => setOxygenSubTab(newValue)}
              sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
              variant="standard"
            >
              <Tab label="Plants" />
              <Tab label="Readings" />
            </Tabs>

            {oxygenSubTab === 0 && (
              <DataTable
                columns={[...oxygenPlantColumns, oxygenPlantActionsColumn]}
                data={oxygenPlants}
                loading={loading}
                searchPlaceholder="Search oxygen plants..."
                emptyMessage="No oxygen plants found"
              />
            )}

            {oxygenSubTab === 1 && (
              <DataTable
                columns={[...oxygenPlantReadingColumns, oxygenReadingActionsColumn]}
                data={oxygenPlantReadings}
                loading={loading}
                searchPlaceholder="Search oxygen readings..."
                emptyMessage="No oxygen plant readings found"
              />
            )}
          </Box>
        )}
      </Card>

      <BuildingForm
        open={buildingFormOpen}
        onClose={() => {
          setBuildingFormOpen(false)
          setSelectedBuilding(null)
        }}
        building={selectedBuilding}
        onSuccess={fetchData}
      />

      <GeneratorForm
        open={generatorFormOpen}
        onClose={() => {
          setGeneratorFormOpen(false)
          setSelectedGenerator(null)
        }}
        generator={selectedGenerator}
        onSuccess={fetchData}
      />

      {/* Utility Reading Form Modal */}
      <Modal
        open={utilityFormOpen}
        onClose={() => setUtilityFormOpen(false)}
        title={selectedReading ? 'Edit Utility Reading' : 'Add Utility Reading'}
        maxWidth="sm"
        actions={
          <>
            <Button variant="outlined" onClick={() => setUtilityFormOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveUtilityReading} loading={saving}>
              {selectedReading ? 'Update' : 'Create'}
            </Button>
          </>
        }
      >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required>
              <InputLabel>Utility Type</InputLabel>
              <Select
                value={utilityFormData.utility}
                onChange={(e) => {
                  const utilityType = UTILITY_TYPES.find((t) => t.value === e.target.value)
                  setUtilityFormData({
                    ...utilityFormData,
                    utility: e.target.value,
                    unit: utilityType?.unit || utilityFormData.unit,
                  })
                }}
                label="Utility Type"
              >
                {utilities.map((u) => (
                  <MenuItem key={u.id} value={u.id}>
                    {UTILITY_TYPES.find((t) => t.value === u.name)?.label || u.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required>
              <InputLabel>Building</InputLabel>
              <Select
                value={utilityFormData.building}
                onChange={(e) => setUtilityFormData({ ...utilityFormData, building: e.target.value })}
                label="Building"
              >
                {buildings.map((b) => (
                  <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              label="Reading Value"
              type="number"
              value={utilityFormData.reading_value}
              onChange={(e) => setUtilityFormData({ ...utilityFormData, reading_value: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              label="Unit"
              value={utilityFormData.unit}
              onChange={(e) => setUtilityFormData({ ...utilityFormData, unit: e.target.value })}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              required
              label="Reading Date"
              type="date"
              value={utilityFormData.reading_date}
              onChange={(e) => setUtilityFormData({ ...utilityFormData, reading_date: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Notes"
              value={utilityFormData.notes}
              onChange={(e) => setUtilityFormData({ ...utilityFormData, notes: e.target.value })}
              multiline
              rows={2}
            />
          </Grid>
        </Grid>
      </Modal>

      {/* Oxygen Plant Form Modal */}
      <Modal
        open={oxygenPlantFormOpen}
        onClose={() => setOxygenPlantFormOpen(false)}
        title={selectedOxygenPlant ? 'Edit Oxygen Plant' : 'Add Oxygen Plant'}
        maxWidth="sm"
        actions={
          <>
            <Button variant="outlined" onClick={() => setOxygenPlantFormOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveOxygenPlant} loading={saving}>
              {selectedOxygenPlant ? 'Update' : 'Create'}
            </Button>
          </>
        }
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              required
              label="Plant Name"
              value={oxygenPlantFormData.name}
              onChange={(e) => setOxygenPlantFormData({ ...oxygenPlantFormData, name: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Location</InputLabel>
              <Select
                value={oxygenPlantFormData.location}
                onChange={(e) => setOxygenPlantFormData({ ...oxygenPlantFormData, location: e.target.value })}
                label="Location"
              >
                <MenuItem value="">None</MenuItem>
                {locations.map((loc) => (
                  <MenuItem key={loc.id} value={loc.id}>{loc.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              label="Capacity"
              placeholder="e.g., 500 L/min"
              value={oxygenPlantFormData.capacity}
              onChange={(e) => setOxygenPlantFormData({ ...oxygenPlantFormData, capacity: e.target.value })}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={oxygenPlantFormData.status}
                onChange={(e) => setOxygenPlantFormData({ ...oxygenPlantFormData, status: e.target.value })}
                label="Status"
              >
                {OXYGEN_PLANT_STATUS.map((s) => (
                  <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Modal>

      {/* Oxygen Plant Reading Form Modal */}
      <Modal
        open={oxygenReadingFormOpen}
        onClose={() => setOxygenReadingFormOpen(false)}
        title={selectedOxygenReading ? 'Edit Oxygen Reading' : 'Add Oxygen Reading'}
        maxWidth="sm"
        actions={
          <>
            <Button variant="outlined" onClick={() => setOxygenReadingFormOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveOxygenReading} loading={saving}>
              {selectedOxygenReading ? 'Update' : 'Create'}
            </Button>
          </>
        }
      >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required>
              <InputLabel>Oxygen Plant</InputLabel>
              <Select
                value={oxygenReadingFormData.plant}
                onChange={(e) => setOxygenReadingFormData({ ...oxygenReadingFormData, plant: e.target.value })}
                label="Oxygen Plant"
              >
                {oxygenPlants.map((p) => (
                  <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              label="Reading Date/Time"
              type="datetime-local"
              value={oxygenReadingFormData.reading_date}
              onChange={(e) => setOxygenReadingFormData({ ...oxygenReadingFormData, reading_date: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Output Pressure (PSI)"
              type="number"
              inputProps={{ step: '0.01' }}
              value={oxygenReadingFormData.output_pressure}
              onChange={(e) => setOxygenReadingFormData({ ...oxygenReadingFormData, output_pressure: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Purity (%)"
              type="number"
              inputProps={{ step: '0.01', min: 0, max: 100 }}
              value={oxygenReadingFormData.purity_percentage}
              onChange={(e) => setOxygenReadingFormData({ ...oxygenReadingFormData, purity_percentage: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Production Volume (L)"
              type="number"
              inputProps={{ step: '0.01' }}
              value={oxygenReadingFormData.production_volume}
              onChange={(e) => setOxygenReadingFormData({ ...oxygenReadingFormData, production_volume: e.target.value })}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Notes"
              value={oxygenReadingFormData.notes}
              onChange={(e) => setOxygenReadingFormData({ ...oxygenReadingFormData, notes: e.target.value })}
              multiline
              rows={2}
            />
          </Grid>
        </Grid>
      </Modal>

      <Modal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title={getDeleteTitle()}
        maxWidth="xs"
        actions={
          <>
            <Button variant="outlined" onClick={() => setDeleteModalOpen(false)} disabled={deleting}>
              Cancel
            </Button>
            <Button color="error" onClick={handleDelete} loading={deleting}>
              Delete
            </Button>
          </>
        }
      >
        Are you sure you want to delete "{getDeleteItemName()}"?
      </Modal>
    </Box>
  )
}

export default FacilitiesList
